<script setup lang="ts">
import Dialog from '../Dialog/Dialog.vue';
import { Measurement } from './types';
import { computed, ref, watch } from 'vue';
import { useMeasurement } from './utils/useMeasurement';
import RTSystemTile from '../RTSystemTile/RTSystemTile.vue';
import Notification from '../notifications/Notification.vue';
import { ResultDto } from '@/api/generator';
import { sortedQaEquipment } from '@/views/jobConfigurations/utils/sortedQaEquipment';
import ConfirmedAction from '../confirmationModals/ConfirmedAction.vue';
import { DialogSize } from '../Dialog/types';
import { useBackendReachable } from '@/utils/useBackendReachable';
import { truthy } from '@/utils/helper';
import { NotificationEntry } from '../notifications/types';
import { v7 as uuidv7 } from 'uuid';
import { DateTime } from 'luxon';
import { getNotificationDetails } from '../notifications/utils';
import { useTranslation } from 'i18next-vue';

const { t } = useTranslation();

export interface MeasurementDialogProps {
  open: boolean;
  measurements?: Measurement[];
  title?: string;
  size?: DialogSize;
}

const props = withDefaults(defineProps<MeasurementDialogProps>(), {
  size: 'large',
  measurements: undefined,
  title: undefined,
});

const emits = defineEmits<{
  'update:open': [open: boolean];
  'measurement:finished': [measurementId: string];
}>();

const defaultIndex = -1;

const currentMeasurementIndex = ref<number>(defaultIndex);

const results = ref<Record<number, ResultDto | undefined>>({});

const allMeasurementsDone = computed(
  () =>
    Object.values(results.value).length ===
    props.measurements?.filter((measurement) => !measurement.disabled && !measurement.skip).length,
);

const measurementProps = useMeasurement(
  computed(() => props.measurements?.[currentMeasurementIndex.value]),
);

const {
  start: startMeasurement,
  reset: resetMeasurement,
  onFinish,
  measurementId,
  status,
  cancel,
  measurement: currentMeasurement,
  isMeasuring,
  notifications: measurementNotifications,
} = measurementProps;

const hasError = computed(() => status.value?.measurementStatus === 'Error');

const isRepeatable = computed(() => {
  return status.value?.isRepeatable && (hasError.value || allMeasurementsDone.value);
});

function selectFirst() {
  const firstIndex = props.measurements?.findIndex((m) => !m.disabled) ?? -1;
  currentMeasurementIndex.value = Math.max(firstIndex, 0);
}

function reset() {
  resetMeasurement();
  selectFirst();
  results.value = {};
}

function shouldExecute(measurement?: Measurement) {
  return !measurement?.skip && !measurement?.disabled;
}

function wasMeasurementAlreadyExecuted(index: number) {
  return index in results.value;
}

async function start() {
  if (
    shouldExecute(currentMeasurement.value) &&
    !wasMeasurementAlreadyExecuted(currentMeasurementIndex.value)
  ) {
    resetMeasurement();
    await startMeasurement();
  }
}

async function repeat() {
  if (isRepeatable.value) {
    reset();
    start();
  }
}

async function next() {
  const { done } = setNext();

  if (!done) {
    await start();
  }
}

function setNext(oldIndex: number = currentMeasurementIndex.value) {
  const nextIndex = props.measurements?.findIndex((measurement, index) => {
    return index > oldIndex && shouldExecute(measurement);
  });

  const done = nextIndex === undefined || nextIndex < 0;

  if (!done) {
    currentMeasurementIndex.value =
      nextIndex !== undefined && nextIndex >= 0 ? nextIndex : oldIndex;
  }

  return { done };
}

function close(open: boolean = false) {
  if (!open) {
    cancel();

    emits('update:open', false);

    reset();
  }
}

function addResult(result?: ResultDto) {
  const index = currentMeasurementIndex.value;
  results.value = { ...results.value, [index]: result };
}

watch(
  () => props.open,
  () => {
    if (props.open) {
      reset();
      start();
    }
  },
  { immediate: true },
);

onFinish((result) => {
  addResult(result);

  if (measurementId.value) {
    emits('measurement:finished', measurementId.value);
    next();
  }
});

const backendNotReachableNotification = ref<NotificationEntry>();

const { onConnectionLost } = useBackendReachable({
  method: 'signalR',
});

onConnectionLost(() => {
  backendNotReachableNotification.value = {
    id: uuidv7(),
    time: DateTime.now(),
    title: t('Warning_Message_Measurement_ConnectionLost'),
    message: getNotificationDetails(
      t('Warning_Cause_Measurement_ConnectionLost'),
      t('Warning_Remedy_Measurement_ConnectionLost'),
    ),
    type: 'warning',
    closable: false,
  };
});

const notifications = computed(() =>
  [backendNotReachableNotification.value, ...measurementNotifications.value].filter(truthy),
);
</script>

<template>
  <ConfirmedAction :needsConfirmation="isMeasuring" @confirmed="close(false)">
    <template #default="{ confirmedAction }">
      <Dialog :modelValue="open" :size="size" @update:modelValue="confirmedAction">
        <template #title>
          {{ title }}
        </template>

        <template #text>
          <v-row>
            <v-col>
              <slot name="instructions" />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <RTSystemTile
                v-if="currentMeasurement?.targetSystem"
                :item="{
                  ...currentMeasurement?.targetSystem,
                  serialNumber: undefined,
                }"
              />
              <v-divider vertical />
            </v-col>
            <v-col>
              <RTSystemTile
                v-for="(supportSystem, index) in sortedQaEquipment(
                  currentMeasurement?.supportSystems,
                )"
                :key="supportSystem.id"
                :item="supportSystem"
                :class="
                  currentMeasurement && index < currentMeasurement.supportSystems.length - 1
                    ? 'mb-2'
                    : ''
                "
              />
            </v-col>
          </v-row>

          <v-row>
            <div class="d-flex flex-column w-100">
              <Notification
                v-for="notification in notifications"
                :key="notification.id"
                :value="notification"
                class="mb-4 ml-4 mr-4"
              />
            </div>
          </v-row>

          <slot
            name="measurements"
            :measurements="measurements"
            :results="results"
            :finished="allMeasurementsDone"
            v-bind="measurementProps"
          />
        </template>

        <template #actions>
          <v-row>
            <v-col align="right">
              <slot
                name="actions"
                v-bind="measurementProps"
                :start="start"
                :repeat="repeat"
                :isRepeatable="isRepeatable"
                :reset="reset"
                :finished="allMeasurementsDone"
                :close="confirmedAction"
                :isMeasuring="isMeasuring"
              />
            </v-col>
          </v-row>
        </template>
      </Dialog>
    </template>

    <template #dialog.text>
      {{ $t('JobExecutionCloseMeasurementConfirmationMsg') }}
    </template>
  </ConfirmedAction>
</template>
